/**
 * component-enquiry-form.js
 *
 * Handles enquiry modal open/close state driven by Shopify's
 * native contact form POST response:
 *
 *   Success → Shopify redirects to ?contact_posted=true
 *             → JS opens modal in success view, cleans URL
 *
 *   Error   → Shopify re-renders page with form.errors in Liquid
 *             → Liquid sets data-enquiry-has-errors on the modal element
 *             → JS opens modal so user sees inline errors
 *
 *   Client-side validation prevents obviously bad submissions
 *   from hitting the server at all.
 */

(function () {
  'use strict';

  class EnquiryFormController {
    constructor(modalEl) {
      this.modal = modalEl;
      this.form = modalEl.querySelector('#EnquiryForm');
      this.modalId = modalEl.id; // 'enquiry-modal'

      if (!this.form) return;

      this._handlePageLoad();
    }

    // ─── Page load ────────────────────────────────────────────────────────────

    _handlePageLoad() {
      const params = new URLSearchParams(window.location.search);
      this.modal.addEventListener('modal-close', () => this._showView('form'));

      if (params.get('contact_posted') === 'true') {
        // Success: Shopify confirmed the submission
        this._openModal();
        this._showView('success');
        this._cleanUrl(params);
        return;
      }

      // Error: Shopify re-rendered the page with form.errors.
      // Liquid sets data-enquiry-has-errors="true" on the modal element
      // (see enquiry-modal-component.liquid).
      if (this.modal.dataset.enquiryHasErrors === 'true') {
        this._openModal();
        this._showView('form');
      }
    }

    // ─── View management ──────────────────────────────────────────────────────

    _showView(view) {
      this.modal.querySelectorAll('[data-enquiry-view]').forEach((el) => {
        const shouldHide = el.dataset.enquiryView !== view;
        if (shouldHide) el.classList.add('hidden');
        else el.classList.remove('hidden');
      });
    }

    _openModal() {
      // Reuse the existing ModalComponent registry — no new open() logic
      const modalComponent = new ModalComponent(this.modal);
      modalComponent?.open();
    }

    // ─── URL cleanup ─────────────────────────────────────────────────────────

    /**
     * Remove contact_posted from the URL so a page refresh
     * doesn't re-trigger the success view.
     */
    _cleanUrl(params) {
      params.delete('contact_posted');
      const clean = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', clean);
    }
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', () => {
    const modalEl = document.querySelector('[data-enquiry-modal]');
    if (modalEl) new EnquiryFormController(modalEl);
  });
})();
